import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { renderTemplate, DEFAULT_TEMPLATES, waLink } from "@/lib/constants";
import { Gear, WhatsappLogo, Clock, UserCircle, Lock, ChatText, PaperPlaneTilt, SpeakerHigh, Play, MapPin } from "@phosphor-icons/react";
import { playNotifChime, unlockAudio, speakAnnouncement, CHIME_TONES } from "@/lib/notifChime";
import BookingMaintenancePanel from "@/components/BookingMaintenancePanel";
import AutoConfirmPanel from "@/components/AutoConfirmPanel";
import AssistantTokenPanel from "@/components/AssistantTokenPanel";

const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100 disabled:bg-slate-50 disabled:text-slate-400";
const labelCls = "mb-1 block text-xs font-semibold text-slate-500";
const cardCls = "rounded-2xl border border-rose-100/70 bg-white p-5 shadow-sm";
const btnCls = "rounded-xl bg-[#7C4DE0] px-6 py-2.5 text-sm font-bold text-white hover:bg-[#6A38C7] disabled:opacity-60";

export default function Settings() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const canNotify = user?.role === "super_admin" || user?.role === "admin";

  const [profile, setProfile] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);
  const [soundOn, setSoundOn] = useState(() => (localStorage.getItem("gbs_notif_on") ?? "on") === "on");
  const [soundVol, setSoundVol] = useState(() => Math.round(parseFloat(localStorage.getItem("gbs_notif_vol") ?? "0.9") * 100));
  const [soundTone, setSoundTone] = useState(() => localStorage.getItem("gbs_notif_tone") || "bandara");
  const [chimeOn, setChimeOn] = useState(() => (localStorage.getItem("gbs_notif_chime") ?? "on") === "on");
  const [voiceOn, setVoiceOn] = useState(() => (localStorage.getItem("gbs_notif_voice") ?? "off") === "on");
  const [voiceMode, setVoiceMode] = useState(() => localStorage.getItem("gbs_notif_voice_mode") || "name");

  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingTpl, setSavingTpl] = useState(false);

  const SAMPLE = { nama: "Ibu Sarah", layanan: "Prenatal Yoga Reguler", kategori: "Prenatal Yoga", tanggal: "Senin, 12 Agustus 2026", jam: "09:00", lokasi: "Gerai GBS", petugas: "Bidan Neny" };

  const testWA = () => {
    const wa = (profile.whatsapp || "").replace(/[^0-9]/g, "");
    if (!wa) return toast.error("Isi & simpan Nomor WhatsApp admin dulu");
    const msg = "Halo, ini pesan uji dari GBS Clinic. Jika Anda menerima pesan ini, berarti nomor WhatsApp notifikasi reservasi sudah aktif ✅";
    window.open(waLink(wa, msg), "_blank");
  };

  const resetTpl = (key) => setForm((f) => ({ ...f, [key]: DEFAULT_TEMPLATES[key] }));

  const saveTemplates = async () => {
    setSavingTpl(true);
    try {
      const payload = {
        tpl_confirm: form.tpl_confirm ?? DEFAULT_TEMPLATES.tpl_confirm,
        tpl_reminder: form.tpl_reminder ?? DEFAULT_TEMPLATES.tpl_reminder,
        tpl_approved: form.tpl_approved ?? DEFAULT_TEMPLATES.tpl_approved,
        tpl_rejected: form.tpl_rejected ?? DEFAULT_TEMPLATES.tpl_rejected,
        tpl_reschedule: form.tpl_reschedule ?? DEFAULT_TEMPLATES.tpl_reschedule,
      };
      const { data } = await api.put("/settings/templates", payload);
      toast.success("Template pesan tersimpan");
      setForm({ ...form, ...data });
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingTpl(false); }
  };

  useEffect(() => {
    api.get("/auth/me").then((r) => setProfile(r.data)).catch(() => setProfile({}));
    api.get("/public/settings").then((r) => setForm(r.data)).catch(() => setForm({}));
  }, []);

  const setP = (k, v) => setProfile((p) => ({ ...p, [k]: v }));
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const saveProfile = async () => {
    if (!profile.name?.trim()) return toast.error("Nama tidak boleh kosong");
    if (!profile.email?.trim()) return toast.error("Email tidak boleh kosong");
    setSavingProfile(true);
    try {
      const payload = {
        name: profile.name.trim(),
        email: profile.email.trim(),
        whatsapp: (profile.whatsapp || "").replace(/[^0-9]/g, ""),
      };
      const { data } = await api.put("/auth/profile", payload);
      setProfile(data);
      toast.success("Profil admin tersimpan");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingProfile(false); }
  };

  const savePw = async () => {
    if (!pw.current_password) return toast.error("Masukkan password saat ini");
    if (pw.new_password.length < 6) return toast.error("Password baru minimal 6 karakter");
    if (pw.new_password !== pw.confirm) return toast.error("Konfirmasi password tidak cocok");
    setSavingPw(true);
    try {
      await api.put("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password berhasil diperbarui");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingPw(false); }
  };

  const save = async () => {
    if ((form.jam_tutup || "") <= (form.jam_buka || "")) return toast.error("Jam tutup harus setelah jam buka");
    if (Number(form.slot_menit || 0) <= 0) return toast.error("Durasi slot harus lebih dari 0 menit");
    setSaving(true);
    try {
      const payload = {
        clinic_name: form.clinic_name || "",
        clinic_wa: (form.clinic_wa || "").replace(/[^0-9]/g, ""),
        alamat: form.alamat || "",
        maps_url: (form.maps_url || "").trim(),
        jam_buka: form.jam_buka || "08:00",
        jam_tutup: form.jam_tutup || "20:00",
        slot_menit: Number(form.slot_menit || 30),
      };
      await api.put("/settings", payload);
      toast.success("Pengaturan klinik disimpan");
      setForm({ ...form, ...payload });
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  if (!profile || !form) return null;

  return (
    <div className="animate-fade-up space-y-5" data-testid="settings-page">
      <div className="flex items-center gap-2">
        <Gear size={26} weight="duotone" className="text-[#7C4DE0]" />
        <h1 className="font-heading text-3xl font-extrabold text-slate-800">Pengaturan</h1>
      </div>

      <div className="max-w-xl space-y-5">
        {isSuper && <BookingMaintenancePanel />}
        {isSuper && <AutoConfirmPanel />}
        {isSuper && <AssistantTokenPanel />}
        {/* Profil Admin */}
        <div className={cardCls} data-testid="profile-card">
          <div className="mb-4 flex items-center gap-2">
            <UserCircle size={18} weight="duotone" className="text-[#7C4DE0]" />
            <h3 className="font-heading font-bold text-slate-800">Profil Akun</h3>
          </div>
          <div className="space-y-3">
            <div>
              <label className={labelCls}>Nama Admin</label>
              <input data-testid="profile-name" value={profile.name || ""} onChange={(e) => setP("name", e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Email (untuk login)</label>
              <input data-testid="profile-email" type="email" value={profile.email || ""} onChange={(e) => setP("email", e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Nomor WhatsApp / HP Admin</label>
              <input data-testid="profile-whatsapp" value={profile.whatsapp || ""} onChange={(e) => setP("whatsapp", e.target.value)} className={inputCls} placeholder="cth. 081234567890" />
              {canNotify && (
                <p className="mt-1 text-xs text-emerald-600">Nomor ini menjadi tujuan notifikasi / konfirmasi reservasi pasien.</p>
              )}
              {canNotify && (
                <button type="button" data-testid="test-wa" onClick={testWA} className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-emerald-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-600">
                  <PaperPlaneTilt size={14} weight="fill" /> Uji Kirim WA
                </button>
              )}
            </div>
            <button data-testid="save-profile" onClick={saveProfile} disabled={savingProfile} className={btnCls}>
              {savingProfile ? "Menyimpan..." : "Simpan Profil"}
            </button>
          </div>
        </div>

        {/* Notifikasi Suara Booking Masuk (per perangkat) */}
        <div className={cardCls} data-testid="notif-sound-card">
          <div className="mb-4 flex items-center gap-2">
            <SpeakerHigh size={18} weight="duotone" className="text-[#EC4899]" />
            <h3 className="font-heading font-bold text-slate-800">Notifikasi Suara Booking Masuk</h3>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">🔊 Suara Booking Masuk</span>
              <button data-testid="notif-sound-toggle" onClick={() => { const n = !soundOn; setSoundOn(n); localStorage.setItem("gbs_notif_on", n ? "on" : "off"); }}
                className={`rounded-full px-3 py-1 text-xs font-bold ${soundOn ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{soundOn ? "ON" : "OFF"}</button>
            </div>
            <div>
              <label className={labelCls}>🔉 Volume Notifikasi ({soundVol}%)</label>
              <input type="range" min="0" max="100" step="5" data-testid="notif-sound-volume" value={soundVol}
                onChange={(e) => { const v = Number(e.target.value); setSoundVol(v); localStorage.setItem("gbs_notif_vol", String(v / 100)); }}
                className="w-full accent-[#EC4899]" />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">🔔 Chime (bunyi nada)</span>
              <button data-testid="notif-chime-toggle" onClick={() => { const n = !chimeOn; setChimeOn(n); localStorage.setItem("gbs_notif_chime", n ? "on" : "off"); }}
                className={`rounded-full px-3 py-1 text-xs font-bold ${chimeOn ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{chimeOn ? "ON" : "OFF"}</button>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">🗣️ Voice Announcement (TTS)</span>
              <button data-testid="notif-voice-toggle" onClick={() => { const n = !voiceOn; setVoiceOn(n); localStorage.setItem("gbs_notif_voice", n ? "on" : "off"); }}
                className={`rounded-full px-3 py-1 text-xs font-bold ${voiceOn ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{voiceOn ? "ON" : "OFF"}</button>
            </div>
            {voiceOn && (
              <div>
                <label className={labelCls}>Isi suara pengumuman</label>
                <div className="space-y-1.5">
                  {[["name", "Nama pasien saja (contoh: “Ada booking baru dari Siti.”)"], ["name_service", "Nama pasien + layanan"], ["generic", "Privasi: “Ada booking baru masuk.” (tanpa nama)"]].map(([id, lbl]) => (
                    <label key={id} className="flex items-start gap-2 text-sm text-slate-600">
                      <input type="radio" name="voice_mode" data-testid={`notif-voice-mode-${id}`} checked={voiceMode === id}
                        onChange={() => { setVoiceMode(id); localStorage.setItem("gbs_notif_voice_mode", id); }} className="mt-1" />
                      <span>{lbl}</span>
                    </label>
                  ))}
                </div>
                <p className="mt-1 text-[11px] text-slate-400">Demi privasi, pengumuman tidak pernah menyebut No. HP, NIK, alamat, keluhan, data kehamilan, atau nominal pembayaran.</p>
              </div>
            )}
            <div>
              <label className={labelCls}>🎵 Nada Chime (ketuk untuk pratinjau)</label>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                {CHIME_TONES.map((t) => (
                  <button key={t.id} type="button" data-testid={`notif-tone-${t.id}`}
                    onClick={() => { setSoundTone(t.id); localStorage.setItem("gbs_notif_tone", t.id); unlockAudio(); playNotifChime(soundVol / 100, t.id); }}
                    className={`rounded-xl border px-3 py-2.5 text-xs font-bold transition-colors ${soundTone === t.id ? "border-[#EC4899] bg-[#FCE7F3] text-[#DB2777]" : "border-slate-200 bg-white text-slate-600 hover:border-[#EC4899]"}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <button type="button" data-testid="notif-sound-test" onClick={() => { unlockAudio(); if (chimeOn) playNotifChime(soundVol / 100, soundTone); if (voiceOn) { const ph = voiceMode === "generic" ? "Ada booking baru masuk." : voiceMode === "name_service" ? "Ada booking baru dari Siti untuk Prenatal Yoga." : "Ada booking baru dari Siti."; setTimeout(() => speakAnnouncement(ph, soundVol / 100), chimeOn ? 2700 : 200); } }}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#EC4899] px-4 py-2 text-xs font-bold text-white hover:bg-[#DB2777]">
              <Play size={14} weight="fill" /> ▶️ Test Suara
            </button>
            <p className="text-xs text-slate-400">Suara chime berbunyi 1× hanya untuk booking baru yang benar-benar masuk (tidak berbunyi saat halaman di-refresh atau membuka booking lama). Sebagian browser/iPhone memerlukan Anda menekan <b>Test Suara</b> sekali agar audio diizinkan.</p>
          </div>
        </div>

        {/* Ganti Password */}
        <div className={cardCls} data-testid="password-card">
          <div className="mb-4 flex items-center gap-2">
            <Lock size={18} weight="duotone" className="text-[#6A38C7]" />
            <h3 className="font-heading font-bold text-slate-800">Ganti Password</h3>
          </div>
          <div className="space-y-3">
            <div>
              <label className={labelCls}>Password Saat Ini</label>
              <input data-testid="pw-current" type="password" value={pw.current_password} onChange={(e) => setPw((s) => ({ ...s, current_password: e.target.value }))} className={inputCls} placeholder="••••••••" />
            </div>
            <div>
              <label className={labelCls}>Password Baru (min. 6 karakter)</label>
              <input data-testid="pw-new" type="password" value={pw.new_password} onChange={(e) => setPw((s) => ({ ...s, new_password: e.target.value }))} className={inputCls} placeholder="••••••••" />
            </div>
            <div>
              <label className={labelCls}>Konfirmasi Password Baru</label>
              <input data-testid="pw-confirm" type="password" value={pw.confirm} onChange={(e) => setPw((s) => ({ ...s, confirm: e.target.value }))} className={inputCls} placeholder="••••••••" />
            </div>
            <button data-testid="save-password" onClick={savePw} disabled={savingPw} className={btnCls}>
              {savingPw ? "Menyimpan..." : "Perbarui Password"}
            </button>
          </div>
        </div>

        {/* Template Pesan WhatsApp */}
        {canNotify && (
          <div className={cardCls} data-testid="templates-card">
            <div className="mb-1 flex items-center gap-2"><ChatText size={18} weight="duotone" className="text-[#7C4DE0]" /><h3 className="font-heading font-bold text-slate-800">Template Pesan WhatsApp</h3></div>
            <p className="mb-3 text-xs text-slate-400">Placeholder tersedia: <code className="rounded bg-slate-100 px-1">{"{nama}"}</code> <code className="rounded bg-slate-100 px-1">{"{layanan}"}</code> <code className="rounded bg-slate-100 px-1">{"{kategori}"}</code> <code className="rounded bg-slate-100 px-1">{"{tanggal}"}</code> <code className="rounded bg-slate-100 px-1">{"{jam}"}</code> <code className="rounded bg-slate-100 px-1">{"{lokasi}"}</code> <code className="rounded bg-slate-100 px-1">{"{petugas}"}</code></p>
            <div className="space-y-5">
              {[
                { key: "tpl_confirm", label: "Konfirmasi Booking (pasien → admin)", rows: 8 },
                { key: "tpl_reminder", label: "Pengingat H-1 (ke pasien)", rows: 11 },
                { key: "tpl_approved", label: "Konfirmasi Disetujui (ke pasien)", rows: 9 },
                { key: "tpl_rejected", label: "Penolakan / Batal (ke pasien)", rows: 8 },
                { key: "tpl_reschedule", label: "Perubahan Jadwal (ke pasien)", rows: 8 },
              ].map(({ key, label, rows }) => (
                <div key={key}>
                  <div className="mb-1 flex items-center justify-between">
                    <label className={labelCls + " mb-0"}>{label}</label>
                    <button type="button" data-testid={`reset-${key}`} onClick={() => resetTpl(key)} className="text-xs font-semibold text-[#6A38C7] hover:underline">Reset ke default</button>
                  </div>
                  <textarea data-testid={`tpl-${key}`} rows={rows} value={form[key] ?? DEFAULT_TEMPLATES[key]} onChange={(e) => set(key, e.target.value)} className={inputCls + " font-mono text-xs leading-relaxed"} />
                  <div className="mt-1.5 rounded-xl bg-[#F7F0FF] p-3">
                    <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">Pratinjau</p>
                    <p data-testid={`preview-${key}`} className="whitespace-pre-wrap break-words text-xs text-slate-700">{renderTemplate(form[key] ?? DEFAULT_TEMPLATES[key], SAMPLE)}</p>
                  </div>
                </div>
              ))}
              <button data-testid="save-templates" onClick={saveTemplates} disabled={savingTpl} className={btnCls}>{savingTpl ? "Menyimpan..." : "Simpan Template"}</button>
            </div>
          </div>
        )}

        {/* Pengaturan Klinik (Super Admin) */}
        <div className="pt-1">
          <p className="mb-2 px-1 text-xs font-bold uppercase tracking-widest text-slate-400">Pengaturan Klinik</p>
          {!isSuper && <p className="mb-3 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-700">Pengaturan klinik hanya dapat diubah oleh Super Admin.</p>}
        </div>

        <div className={cardCls}>
          <div className="mb-4 flex items-center gap-2"><WhatsappLogo size={18} weight="duotone" className="text-emerald-600" /><h3 className="font-heading font-bold text-slate-800">Identitas Klinik</h3></div>
          <div>
            <label className={labelCls}>Nama Klinik</label>
            <input data-testid="setting-clinic-name" disabled={!isSuper} value={form.clinic_name || ""} onChange={(e) => set("clinic_name", e.target.value)} className={inputCls} />
          </div>
        </div>

        <div className={cardCls}>
          <div className="mb-4 flex items-center gap-2"><MapPin size={18} weight="duotone" className="text-[#DB2777]" /><h3 className="font-heading font-bold text-slate-800">Lokasi Klinik</h3></div>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className={labelCls}>Alamat Klinik</label>
              <input data-testid="setting-alamat" disabled={!isSuper} value={form.alamat || ""} onChange={(e) => set("alamat", e.target.value)} className={inputCls} placeholder="cth: Jl. Contoh No. 1, Samarinda" />
            </div>
            <div>
              <label className={labelCls}>Link Google Maps</label>
              <input data-testid="setting-maps" disabled={!isSuper} value={form.maps_url || ""} onChange={(e) => set("maps_url", e.target.value)} className={inputCls} placeholder="https://maps.google.com/..." />
            </div>
          </div>
          <p className="mt-2 text-xs text-slate-400">Dipakai Asisten Virtual GBS untuk menjawab pertanyaan lokasi. Boleh dikosongkan — bila kosong, asisten akan mengalihkan pertanyaan lokasi ke Admin (tidak mengarang).</p>
        </div>

        <div className={cardCls}>
          <div className="mb-4 flex items-center gap-2"><Clock size={18} weight="duotone" className="text-[#6A38C7]" /><h3 className="font-heading font-bold text-slate-800">Jam Operasional & Slot Jadwal</h3></div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div><label className={labelCls}>Jam Buka</label><input data-testid="setting-jam-buka" disabled={!isSuper} type="time" value={form.jam_buka || "08:00"} onChange={(e) => set("jam_buka", e.target.value)} className={inputCls} /></div>
            <div><label className={labelCls}>Jam Tutup</label><input data-testid="setting-jam-tutup" disabled={!isSuper} type="time" value={form.jam_tutup || "20:00"} onChange={(e) => set("jam_tutup", e.target.value)} className={inputCls} /></div>
            <div><label className={labelCls}>Durasi Slot (menit)</label><input data-testid="setting-slot" disabled={!isSuper} type="number" value={form.slot_menit || 30} onChange={(e) => set("slot_menit", e.target.value)} className={inputCls} /></div>
          </div>
          <p className="mt-2 text-xs text-slate-400">Menentukan pilihan jam yang tersedia di form reservasi publik (per petugas).</p>
        </div>

        {isSuper && (
          <button data-testid="save-settings" onClick={save} disabled={saving} className={btnCls}>{saving ? "Menyimpan..." : "Simpan Pengaturan Klinik"}</button>
        )}
      </div>
    </div>
  );
}
